// 2-step login by email code, required for everyone (real app). Until this sign-in has passed the code step,
// the app shows only this screen; the database refuses everything else anyway (firestore.rules secondStepOk).
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Mail } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useT } from '../../i18n';
import { finishSecondStep, startSecondStep } from '../../services/secondStepService';
import { SurakshaMark } from '../common/Brand';

type State = { kind: 'checking' } | { kind: 'code'; sentTo: string } | { kind: 'done' } | { kind: 'error'; msg: string };

export function EmailCodeScreen({ sentTo, onSubmit, onResend, onSignOut, error, busy }: {
  sentTo: string; onSubmit: (code: string, remember: boolean) => void; onResend: () => void; onSignOut: () => void; error?: string; busy?: boolean;
}) {
  const t = useT();
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(true);
  const submit = (e: FormEvent) => { e.preventDefault(); onSubmit(code, remember); };
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-sc-bg px-5 py-8" data-testid="email-code-screen">
      <form onSubmit={submit} className="w-full max-w-sm">
        <SurakshaMark className="h-12 w-12" />
        <h1 className="mt-5 text-2xl font-bold text-slate-900">{t('code.title')}</h1>
        <p className="mt-2 flex items-start gap-2 text-slate-700"><Mail className="mt-1 h-4 w-4 shrink-0 text-sc-teal" />{t('code.sent', { email: sentTo })}</p>
        <label className="mt-6 block">
          <span className="mb-1.5 block text-sm font-semibold text-slate-700">{t('code.label')}</span>
          <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} data-testid="email-code-input"
            className="h-14 w-full rounded-xl border border-sc-line bg-white px-4 text-center text-3xl font-bold tracking-[0.4em] focus:border-sc-teal focus:outline-none focus:ring-2 focus:ring-sc-teal/30" />
        </label>
        <label className="mt-4 flex items-center gap-3">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="email-code-remember" className="h-6 w-6 shrink-0 accent-[var(--sc-teal)]" />
          <span className="text-slate-800">{t('code.remember')}</span>
        </label>
        {error && <p className="mt-3 rounded-xl bg-status-urgent-soft px-4 py-3 text-sm font-medium text-status-urgent" role="alert" data-testid="email-code-error">{error}</p>}
        <button type="submit" disabled={busy || code.length !== 6} data-testid="email-code-submit" className="mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-sc-teal text-base font-bold text-white disabled:opacity-60">
          {busy && <Loader2 className="h-5 w-5 animate-spin" />}{t('code.submit')}
        </button>
        <div className="mt-4 flex items-center justify-between text-sm">
          <button type="button" onClick={onResend} className="min-h-[44px] font-semibold text-sc-teal underline" data-testid="email-code-resend">{t('code.resend')}</button>
          <button type="button" onClick={onSignOut} className="min-h-[44px] font-semibold text-slate-600">{t('code.signOut')}</button>
        </div>
        <p className="mt-2 text-xs text-slate-500">{t('code.help')}</p>
      </form>
    </div>
  );
}

export function EmailCodeGate({ children }: { children: ReactNode }) {
  const { uid, logout } = useApp();
  const t = useT();
  const qc = useQueryClient();
  const [state, setState] = useState<State>({ kind: 'checking' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const start = async () => {
    if (!uid) return;
    setErr('');
    try {
      const r = await startSecondStep(uid);
      setState(r.done ? { kind: 'done' } : { kind: 'code', sentTo: r.sentTo ?? '' });
      if (r.done) await qc.invalidateQueries();
    } catch (e) {
      const code = e instanceof Error ? e.message : '';
      setState({ kind: 'error', msg: t(code === 'TOO_MANY' ? 'code.tooMany' : 'code.emailFailed') });
    }
  };
  useEffect(() => { void start(); }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.kind === 'done') return <>{children}</>;
  if (state.kind === 'checking') return <div className="flex min-h-screen items-center justify-center bg-sc-bg" data-testid="email-code-checking"><Loader2 className="h-8 w-8 animate-spin text-sc-teal" /></div>;
  if (state.kind === 'error') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-sc-bg px-5 text-center" data-testid="email-code-failed">
        <p className="max-w-sm text-slate-800">{state.msg}</p>
        <button type="button" onClick={() => { setState({ kind: 'checking' }); void start(); }} className="h-12 rounded-xl bg-sc-teal px-5 font-bold text-white">{t('code.tryAgain')}</button>
        <button type="button" onClick={logout} className="text-sm font-semibold text-slate-600">{t('code.signOut')}</button>
      </div>
    );
  }
  return (
    <EmailCodeScreen
      sentTo={state.sentTo}
      error={err}
      busy={busy}
      onSignOut={logout}
      onResend={() => { setState({ kind: 'checking' }); void start(); }}
      onSubmit={async (code, remember) => {
        setBusy(true); setErr('');
        try {
          await finishSecondStep(uid!, code, remember);
          setState({ kind: 'done' });
          await qc.invalidateQueries();
        } catch (e) {
          const m = e instanceof Error ? e.message : '';
          setErr(t(m === 'CODE_EXPIRED' ? 'code.expired' : m === 'CODE_TOO_MANY' ? 'code.locked' : 'code.wrong'));
        } finally { setBusy(false); }
      }}
    />
  );
}
